/**
 * FIELDWEAR Scroll + Dwell Tracker
 * File: assets/fieldwear-tracker.js
 *
 * Tracks per-section scroll depth and dwell time for all fw- sections.
 * Writes events via window.__fwAB.writeEvent() — requires fieldwear-ab.js.
 *
 * Fires:
 * - scroll_depth event when a section reaches 25%, 50%, 75%, 100% visibility
 * - dwell event when visitor spends >8s on a section
 * - micro_interaction events hooked from fieldwear-product.js
 */

(function () {
  'use strict';

  const DWELL_THRESHOLD_MS  = 8000;   // 8 seconds = engaged signal
  const SCROLL_MILESTONES   = [25, 50, 75, 100];
  const SECTION_SELECTOR    = '[class*="fw-"]'; // all fw- sections

  // Track state per section
  const sectionState = new Map();
  // { fired: Set<number>, dwellStart: number|null, dwellTimer: number|null, dwellFired: boolean }

  function getWriteEvent() {
    return window.__fwAB && window.__fwAB.writeEvent ? window.__fwAB.writeEvent : null;
  }

  function getSectionId(el) {
    // Use the first fw- class as identifier
    const match = [...el.classList].find(c => c.startsWith('fw-'));
    return match || el.id || 'unknown';
  }

  function getVisibilityPercent(entry) {
    return Math.round(entry.intersectionRatio * 100);
  }

  // ── Scroll depth observer ─────────────────────────────────

  const scrollObserver = new IntersectionObserver(
    (entries) => {
      const writeEvent = getWriteEvent();

      entries.forEach((entry) => {
        const el        = entry.target;
        const sectionId = getSectionId(el);

        if (!sectionState.has(sectionId)) {
          sectionState.set(sectionId, {
            fired:       new Set(),
            dwellStart:  null,
            dwellTimer:  null,
            dwellFired:  false,
          });
        }

        const state   = sectionState.get(sectionId);
        const pct     = getVisibilityPercent(entry);

        // Fire milestone events
        SCROLL_MILESTONES.forEach((milestone) => {
          if (pct >= milestone && !state.fired.has(milestone)) {
            state.fired.add(milestone);
            if (writeEvent) {
              writeEvent('scroll_depth', {
                section:  sectionId,
                depth_pct: milestone,
              }, milestone >= 75 ? 0.35 : 0.15);
            }
          }
        });

        // Dwell tracking
        if (entry.isIntersecting && pct >= 50) {
          if (!state.dwellStart && !state.dwellFired) {
            state.dwellStart = Date.now();
            state.dwellTimer = setTimeout(() => {
              state.dwellFired = true;
              if (writeEvent) {
                writeEvent('dwell', {
                  section:     sectionId,
                  dwell_ms:    DWELL_THRESHOLD_MS,
                }, 0.25);
              }
            }, DWELL_THRESHOLD_MS);
          }
        } else {
          // Left section — cancel dwell timer
          if (state.dwellTimer) {
            clearTimeout(state.dwellTimer);
            state.dwellTimer = null;
            state.dwellStart = null;
          }
        }
      });
    },
    {
      threshold: [0, 0.25, 0.5, 0.75, 1.0],
    }
  );

  // ── Micro-interaction tracker ─────────────────────────────

  function trackMicroInteraction(action, detail = {}) {
    const writeEvent = getWriteEvent();
    if (!writeEvent) return;
    writeEvent('micro_interaction', {
      action,
      ...detail,
    }, 0.20);
  }

  // Hook into fieldwear-product.js interactions
  // These events are dispatched by fieldwear-product.js via CustomEvent
  document.addEventListener('fw:color_select',   (e) => trackMicroInteraction('color_select',   e.detail));
  document.addEventListener('fw:size_select',    (e) => trackMicroInteraction('size_select',    e.detail));
  document.addEventListener('fw:design_select',  (e) => trackMicroInteraction('design_select',  e.detail));
  document.addEventListener('fw:gallery_swipe',  (e) => trackMicroInteraction('gallery_swipe',  e.detail));
  document.addEventListener('fw:accordion_open', (e) => trackMicroInteraction('accordion_open', e.detail));
  document.addEventListener('fw:size_guide',     (e) => trackMicroInteraction('size_guide_open',e.detail));

  // ── Section registration ──────────────────────────────────

  function registerSections() {
    const sections = document.querySelectorAll(SECTION_SELECTOR);
    sections.forEach((el) => {
      // Only observe direct section wrappers (not nested fw- elements)
      const parent = el.parentElement;
      const parentIsFW = parent && [...parent.classList].some(c => c.startsWith('fw-'));
      if (!parentIsFW) {
        scrollObserver.observe(el);
      }
    });
  }

  // ── Page exit tracking ────────────────────────────────────

  function trackPageExit() {
    const writeEvent = getWriteEvent();
    if (!writeEvent) return;

    // Track sections that got dwell time when page unloads
    window.addEventListener('pagehide', () => {
      sectionState.forEach((state, sectionId) => {
        if (state.dwellStart && !state.dwellFired) {
          const elapsed = Date.now() - state.dwellStart;
          if (elapsed >= 3000) {
            // At least 3s — partial dwell, lower weight
            writeEvent('dwell', {
              section:  sectionId,
              dwell_ms: elapsed,
              partial:  true,
            }, 0.10);
          }
        }
      });
    }, { passive: true });
  }

  // ── Init ──────────────────────────────────────────────────

  function init() {
    if (!('IntersectionObserver' in window)) return; // graceful degradation

    registerSections();
    trackPageExit();

    // Re-register if new sections injected dynamically (e.g. cart drawer)
    const mutationObserver = new MutationObserver(() => {
      registerSections();
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: false,
    });
  }

  // Wait for __fwAB to be ready
  function waitForAB(attempts = 0) {
    if (window.__fwAB) {
      init();
    } else if (attempts < 20) {
      setTimeout(() => waitForAB(attempts + 1), 100);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => waitForAB());
  } else {
    waitForAB();
  }

})();
